/**
 * Settings › Communications — 3-tab redesign (Email · SMS · Reports).
 *
 * Design decisions
 * ────────────────
 * • Templates + Triggers get filtered by channel per tab, so operators
 *   only ever see the surface they care about.
 * • Email tab has a sub-tab dedicated to Mailboxes (the tenant's registered
 *   "From" identities) — each template can optionally bind to one.
 * • SMS tab exposes the configurable per-segment cost that powers the
 *   Reports > SMS spend widget so pricing stays a single source of truth.
 * • Reports tab is intentionally lightweight: two big cards backed by the
 *   `/communications/reports/{email,sms-spend}` endpoints.
 *
 * We intentionally leave the legacy heavy-duty per-template editor
 * (variables, HTML preview, sample data) OUT of this shell — those live
 * behind the "Edit" button on each row and can be built out as its own
 * modal in a follow-up. Every field the backend needs (subject, body,
 * mailbox binding, enabled) is editable inline here so the seed set is
 * fully usable from day 1.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList as TabsListBase, TabsTrigger as TabsTriggerBase, TabsContent as TabsContentBase } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Mail, MessageSquare, BarChart3, Plus, Trash2, Pencil, Save, Star, RefreshCw, CheckCircle2, XCircle, Radio, Zap, Eye, X, ChevronDown } from 'lucide-react';
import { RichTextEditor, RichTextEditorHandle } from '@/components/RichTextEditor';

const TabsList = TabsListBase as unknown as React.FC<{ className?: string; children?: React.ReactNode }>;
const TabsTrigger = TabsTriggerBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode; 'data-testid'?: string }>;
const TabsContent = TabsContentBase as unknown as React.FC<{ value: string; className?: string; children?: React.ReactNode }>;

// ─── Types ─────────────────────────────────────────────────────────────
interface Template {
  id: string;
  name: string;
  channel: 'email' | 'sms' | 'push' | 'whatsapp';
  subject?: string;
  body_text?: string;
  body_html?: string;
  variables?: string[];
  enabled?: boolean;
  description?: string;
  mailbox_key?: string | null;
  is_system?: boolean;
}
interface Trigger {
  id: string;
  key: string;
  name: string;
  description?: string;
  variables?: string[];
  enabled?: boolean;
  bindings?: Record<string, string>;
  is_system?: boolean;
}
interface Mailbox {
  id: string;
  label: string;
  address: string;
  display_name?: string | null;
  reply_to?: string | null;
  is_default: boolean;
  enabled: boolean;
}
interface EmailReport {
  totals: Record<string, number>;
  delivered: number;
  failed: number;
  queued: number;
  recent: Array<Record<string, unknown>>;
}
interface SmsSpend {
  unit_cost_zar: number;
  unit_cost_source?: string;
  total_sent: number;
  total_failed: number;
  total_segments: number;
  total_spend_zar: number;
  currency: string;
  recent: Array<Record<string, unknown>>;
}

// ─── Small helpers ─────────────────────────────────────────────────────
const badge = (b: boolean) => (b
  ? <Badge variant="success">Enabled</Badge>
  : <Badge variant="muted">Disabled</Badge>);

const rowFromApi = <T extends { _id?: string; id?: string }>(r: T): T & { id: string } => ({
  ...r, id: (r.id || r._id) as string,
});

export const CommunicationsSettings: React.FC = () => {
  const [tab, setTab] = useState<'email' | 'sms' | 'reports'>('email');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);

  const load = useCallback(async () => {
    const [t, tr, mb] = await Promise.all([
      api.get<Template[]>('/communications/templates'),
      api.get<Trigger[]>('/communications/triggers'),
      api.get<Mailbox[]>('/communications/mailboxes'),
    ]);
    const byName = (a: { name?: string }, b: { name?: string }) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });
    setTemplates((t.data ?? []).map(rowFromApi).sort(byName));
    setTriggers((tr.data ?? []).map(rowFromApi).sort(byName));
    setMailboxes((mb.data ?? []).map(rowFromApi));
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="communications-page">
      <Card>
        <CardHeader>
          <CardTitle>Communications</CardTitle>
          <CardDescription>Email + SMS templates, triggers, and deliverability reports.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="email" data-testid="comms-tab-email">
                <span className="inline-flex items-center gap-2"><Mail size={14} /> Email</span>
              </TabsTrigger>
              <TabsTrigger value="sms" data-testid="comms-tab-sms">
                <span className="inline-flex items-center gap-2"><MessageSquare size={14} /> SMS</span>
              </TabsTrigger>
              <TabsTrigger value="reports" data-testid="comms-tab-reports">
                <span className="inline-flex items-center gap-2"><BarChart3 size={14} /> Reports</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="email" className="mt-4">
              <EmailTab
                templates={templates.filter((t) => t.channel === 'email')}
                triggers={triggers}
                mailboxes={mailboxes}
                onChanged={load}
              />
            </TabsContent>

            <TabsContent value="sms" className="mt-4">
              <SmsTab
                templates={templates.filter((t) => t.channel === 'sms')}
                triggers={triggers}
                onChanged={load}
              />
            </TabsContent>

            <TabsContent value="reports" className="mt-4">
              <ReportsTab />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// EMAIL TAB
// ═══════════════════════════════════════════════════════════════════════
interface EmailTabProps {
  templates: Template[];
  triggers: Trigger[];
  mailboxes: Mailbox[];
  onChanged: () => Promise<void>;
}
const EmailTab: React.FC<EmailTabProps> = ({ templates, triggers, mailboxes, onChanged }) => {
  const [sub, setSub] = useState<'templates' | 'mailboxes'>('templates');
  const defaultMailbox = mailboxes.find((m) => m.is_default);
  return (
    <Tabs value={sub} onValueChange={(v) => setSub(v as typeof sub)}>
      <TabsList className="grid w-full grid-cols-2 max-w-md">
        <TabsTrigger value="templates" data-testid="email-sub-templates">Templates ({templates.length})</TabsTrigger>
        <TabsTrigger value="mailboxes" data-testid="email-sub-mailboxes">Mailboxes ({mailboxes.length})</TabsTrigger>
      </TabsList>
      <TabsContent value="templates" className="mt-4">
        <TemplateTable
          channel="email"
          templates={templates}
          triggers={triggers}
          mailboxes={mailboxes}
          defaultMailboxLabel={defaultMailbox?.label || 'None set'}
          onChanged={onChanged}
        />
      </TabsContent>
      <TabsContent value="mailboxes" className="mt-4">
        <MailboxesPanel mailboxes={mailboxes} onChanged={onChanged} />
      </TabsContent>
    </Tabs>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// SMS TAB
// ═══════════════════════════════════════════════════════════════════════
interface SmsTabProps { templates: Template[]; triggers: Trigger[]; onChanged: () => Promise<void> }
const SmsTab: React.FC<SmsTabProps> = ({ templates, triggers, onChanged }) => {
  return (
    <div className="mt-2">
      <TemplateTable channel="sms" templates={templates} triggers={triggers} onChanged={onChanged} />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Variable picker — click-to-insert chips
// ═══════════════════════════════════════════════════════════════════════
//
// Reduces cognitive load for non-technical operators. We pool candidate
// variables from three sources so the panel always has something useful:
//
//   1. The template's own declared ``variables`` list.
//   2. The variable set of any trigger that binds to this template on
//      the current channel — those are the vars actually available at
//      dispatch time.
//   3. A curated "common" set (first_name, company_name, login_link,
//      due_date, insurer_name…) so a brand-new template isn't empty.
//
// Duplicates are removed. Variables from source #2 are highlighted as
// "bound" to signal they are guaranteed present when the template fires.
const COMMON_VARIABLES = [
  'first_name', 'full_name', 'email', 'phone',
  'company_name', 'client_name', 'broker_name', 'insurer_name',
  'login_link', 'registration_link', 'contact_link', 'reset_link',
  'due_date', 'site_count', 'otp_code', 'otp_expiry',
  'downtime_start', 'downtime_end',
];

interface VariablePickerProps {
  template: Template;
  triggers: Trigger[];
  channel: 'email' | 'sms';
  onInsert: (varName: string) => void;
}
const VariablePicker: React.FC<VariablePickerProps> = ({ template, triggers, channel, onInsert }) => {
  // Which triggers bind to this template on THIS channel? Those are the
  // dispatch paths the operator's variables will actually see at runtime.
  const boundTriggers = triggers.filter(
    (tr) => (tr.bindings || {})[channel] === template.id && template.id,
  );
  const boundVars = new Set<string>();
  boundTriggers.forEach((tr) => (tr.variables || []).forEach((v) => boundVars.add(v)));

  const declared = new Set(template.variables || []);
  const allTriggerVars = new Set<string>();
  triggers.forEach((tr) => (tr.variables || []).forEach((v) => allTriggerVars.add(v)));

  // Sort: bound (guaranteed) first, then declared, then common, then any
  // extra trigger variables we might not have anticipated.
  const ordered: string[] = [];
  const push = (v: string) => { if (!ordered.includes(v)) ordered.push(v); };
  Array.from(boundVars).sort().forEach(push);
  Array.from(declared).sort().forEach(push);
  COMMON_VARIABLES.forEach(push);
  Array.from(allTriggerVars).sort().forEach(push);

  return (
    <div className="rounded-md border border-border/60 bg-raptor-neutral/20 p-3 space-y-2" data-testid="variable-picker">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-raptor-navy">Insert a variable</div>
        <div className="text-[10px] text-raptor-navy/60">
          Click a chip to insert <code className="text-[10px] bg-raptor-neutral/40 px-1 rounded">{'{{name}}'}</code> at the cursor
          {boundTriggers.length > 0 && <span> · <b>Bound</b> = guaranteed available for {boundTriggers.map((t) => t.name).join(', ')}</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ordered.map((v) => {
          const isBound = boundVars.has(v);
          const isDeclared = declared.has(v);
          return (
            <button
              key={v}
              type="button"
              onClick={() => onInsert(v)}
              data-testid={`var-chip-${v}`}
              className={
                'text-[11px] font-tabular px-2 py-1 rounded-md border transition-colors ' +
                (isBound
                  ? 'border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                  : isDeclared
                    ? 'border-raptor-blue/40 bg-raptor-blue/10 text-raptor-navy hover:bg-raptor-blue/20'
                    : 'border-border/70 bg-white text-raptor-navy/70 hover:bg-raptor-neutral/50')
              }
              title={
                isBound ? 'Guaranteed available when this template fires'
                : isDeclared ? 'Declared on this template'
                : 'Common variable — declare it on the trigger before use'
              }
            >
              {'{{' + v + '}}'}
              {isBound && <span className="ml-1 text-emerald-600">•</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Template list + inline editor
// ═══════════════════════════════════════════════════════════════════════

// Friendly sample value generator for {{variable}} tokens — the preview
// panel seeds each variable input with this so the operator sees a
// plausible email straight away.
const sampleValueFor = (name: string): string => {
  const n = name.toLowerCase();
  if (n.includes('email')) return 'sample.recipient@example.co.za';
  if (n.includes('phone') || n.includes('mobile') || n.includes('cell')) return '+27 82 555 0100';
  if (n.includes('date') || n.includes('due')) return new Date().toLocaleDateString('en-ZA');
  if (n.includes('time')) return '10:30';
  if (n.includes('amount') || n.includes('total') || n.includes('premium')) return 'R 12,345.67';
  if (n.includes('url') || n.includes('link')) return 'https://example.com/link';
  if (n.includes('name') && n.includes('first')) return 'Alex';
  if (n.includes('name') && n.includes('last')) return 'Smith';
  if (n.includes('name')) return 'Alex Smith';
  if (n.includes('id') || n.includes('number') || n.includes('ref')) return 'REF-000123';
  if (n.includes('address')) return '10 Sample Street, Johannesburg';
  if (n.includes('site')) return 'KFC Sunnyside';
  if (n.includes('broker')) return 'Acme Brokers';
  if (n.includes('client')) return 'Acme Retail (Pty) Ltd';
  if (n.includes('insurer')) return 'Osprey UMA';
  return `[${name}]`;
};

// Simple {{variable}} substitution engine — mirrors the backend
// _render() so previews match runtime rendering. Missing variables
// fall back to a highlighted "[missing:name]" placeholder so operators
// can spot un-supplied tokens at a glance.
const renderTemplate = (source: string, vars: Record<string, string>): string => {
  if (!source) return '';
  return source.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, name: string) => {
    const val = vars[name];
    if (val === undefined || val === null || val === '') {
      return `<span style="background:#fef3c7;color:#92400e;padding:0 3px;border-radius:3px;font-family:ui-monospace,monospace;font-size:0.85em;">[missing:${name}]</span>`;
    }
    return val;
  });
};

interface TemplatePreviewProps {
  template: Template;
  triggers: Trigger[];
  channel: 'email' | 'sms';
  sample: Record<string, string>;
  onSampleChange: (next: Record<string, string>) => void;
}

/** Split-pane preview — variable inputs on top, rendered subject + body below. */
const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, triggers, channel, sample, onSampleChange }) => {
  // Union of trigger-bound + template-declared variables — same set
  // the seed effect uses. Rendered here so operators can add missing
  // variables inline (bonus of surfacing gaps between the template's
  // declared list and what the trigger actually promises).
  const bound = new Set<string>();
  triggers.forEach((tr) => {
    if ((tr.bindings || {})[channel] === template.id) (tr.variables || []).forEach((v) => bound.add(v));
  });
  (template.variables || []).forEach((v) => bound.add(v));
  const varList = Array.from(bound).sort();

  const renderedSubject = renderTemplate(template.subject || '', sample);
  const renderedBody = renderTemplate(template.body_html || template.body_text || '', sample);

  return (
    <div className="border border-border/60 rounded-md overflow-hidden flex flex-col bg-white" data-testid="template-preview-panel">
      <div className="bg-raptor-neutral/40 border-b border-border/60 px-3 py-2 flex items-center justify-between">
        <div className="text-xs font-tabular text-raptor-navy/70 uppercase tracking-wider">Preview</div>
        <div className="text-[10px] text-raptor-navy/50">Sample values below</div>
      </div>
      {varList.length > 0 && (
        <div className="border-b border-border/60 p-3 space-y-2 bg-raptor-neutral/10 max-h-40 overflow-y-auto" data-testid="template-preview-vars">
          {varList.map((v) => (
            <div key={v} className="grid grid-cols-[minmax(0,140px)_1fr] gap-2 items-center">
              <code className="text-[11px] font-tabular text-raptor-navy/70 truncate" title={v}>{`{{${v}}}`}</code>
              <Input
                value={sample[v] ?? ''}
                onChange={(e) => onSampleChange({ ...sample, [v]: e.target.value })}
                className="h-7 text-xs"
                data-testid={`template-preview-var-${v}`}
              />
            </div>
          ))}
        </div>
      )}
      <div className="p-3 space-y-2 flex-1 overflow-y-auto">
        {/* Scoped preview styles — mirror the RichTextEditor output. */}
        <style>{`
          [data-testid="template-preview-body"] h1 { font-size: 20px; font-weight: 700; margin: 0.5em 0 0.35em; letter-spacing: -0.01em; }
          [data-testid="template-preview-body"] h2 { font-size: 16px; font-weight: 700; margin: 0.6em 0 0.3em; }
          [data-testid="template-preview-body"] p { margin: 0.35em 0; }
          [data-testid="template-preview-body"] ul { list-style: disc; padding-left: 1.4em; margin: 0.35em 0; }
          [data-testid="template-preview-body"] ol { list-style: decimal; padding-left: 1.5em; margin: 0.35em 0; }
          [data-testid="template-preview-body"] a { color: #0D63FF; text-decoration: underline; }
          [data-testid="template-preview-body"] strong { font-weight: 700; }
          [data-testid="template-preview-body"] em { font-style: italic; }
          [data-testid="template-preview-body"] u { text-decoration: underline; }
        `}</style>
        {template.subject !== undefined && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-raptor-navy/50 font-tabular">Subject</div>
            <div
              className="text-sm font-body font-semibold text-raptor-navy"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderedSubject || '<span style="color:#9ca3af">— no subject —</span>') }}
              data-testid="template-preview-subject"
            />
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-raptor-navy/50 font-tabular">Body</div>
          <div
            className="prose prose-sm max-w-none font-body text-raptor-navy mt-1"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderedBody || '<span style="color:#9ca3af">— empty body —</span>') }}
            data-testid="template-preview-body"
          />
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Template list + inline editor (dialog)
// ═══════════════════════════════════════════════════════════════════════
interface TemplateTableProps {
  channel: 'email' | 'sms';
  templates: Template[];
  triggers?: Trigger[];
  mailboxes?: Mailbox[];
  defaultMailboxLabel?: string;
  onChanged: () => Promise<void>;
}
const TemplateTable: React.FC<TemplateTableProps> = ({ channel, templates, triggers = [], mailboxes = [], defaultMailboxLabel, onChanged }) => {
  const [editing, setEditing] = useState<Template | null>(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  // Per-template "Send test" — dispatches THIS template's rendered
  // content to an operator-supplied email/phone so they can preview it live.
  const [testTarget, setTestTarget] = useState<Template | null>(null);
  const [testTo, setTestTo] = useState('');
  const [testSending, setTestSending] = useState(false);
  // Template preview state — sample values for {{variable}} substitution.
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [sampleVars, setSampleVars] = useState<Record<string, string>>({});
  const [editTriggerKeys, setEditTriggerKeys] = useState<string[]>([]);
  const [triggerSearch, setTriggerSearch] = useState('');
  const [triggerOpen, setTriggerOpen] = useState(false);
  const triggerBoxRef = React.useRef<HTMLDivElement | null>(null);
  // Refs let the variable picker insert {{var}} at the caret position
  // of whichever field the operator most recently interacted with.
  const subjectRef = React.useRef<HTMLInputElement | null>(null);
  const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);
  // Email body is edited via TipTap — we expose an imperative insertText
  // so the variable picker can drop {{tokens}} at the caret.
  const rteRef = React.useRef<RichTextEditorHandle | null>(null);
  const lastFocused = React.useRef<'subject' | 'body'>('body');
  const empty = (): Template => ({ id: '', name: '', channel, subject: '', body_text: '', body_html: '', variables: [], enabled: true, description: '', mailbox_key: null });

  // Seed sample values for the preview panel whenever the editing
  // template changes. Uses trigger-bound + declared variables, filling
  // each with a friendly placeholder derived from the variable name.
  useEffect(() => {
    if (!editing) { setSampleVars({}); return; }
    const bound = new Set<string>();
    triggers.forEach((tr) => {
      if ((tr.bindings || {})[channel] === editing.id) (tr.variables || []).forEach((v) => bound.add(v));
    });
    (editing.variables || []).forEach((v) => bound.add(v));
    setSampleVars((prev) => {
      const next: Record<string, string> = {};
      bound.forEach((v) => {
        // Preserve any values the operator has already tweaked.
        next[v] = prev[v] ?? sampleValueFor(v);
      });
      return next;
    });
    // Seed the "Fires on" selection from the current trigger bindings.
    setEditTriggerKeys(triggers.filter((tr) => (tr.bindings || {})[channel] === editing.id).map((tr) => tr.key));
    setTriggerSearch('');
    setTriggerOpen(false);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [editing?.id]);

  // Close the "Fires on" dropdown when clicking outside the box.
  useEffect(() => {
    if (!triggerOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (triggerBoxRef.current && !triggerBoxRef.current.contains(e.target as Node)) {
        setTriggerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [triggerOpen]);

  const save = async (t: Template) => {
    setSaving(true);
    try {
      const payload: Partial<Template> & { trigger_keys?: string[] } = {
        name: t.name, channel: t.channel, subject: t.subject,
        body_text: t.body_text, body_html: t.body_html,
        variables: t.variables, enabled: t.enabled, description: t.description, mailbox_key: t.mailbox_key,
        trigger_keys: editTriggerKeys,
      };
      let resp: { data?: { warnings?: string[] } };
      if (t.id) {
        resp = await api.patch(`/communications/templates/${t.id}`, payload);
      } else {
        resp = await api.post('/communications/templates', payload);
      }
      const warns = resp?.data?.warnings || [];
      toast.success(editTriggerKeys.length ? `Template saved · fires on ${editTriggerKeys.length} trigger(s)` : 'Template saved');
      warns.forEach((w) => toast.message(w));
      setEditing(null); setCreating(false);
      await onChanged();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed');
    } finally { setSaving(false); }
  };

  const del = async (t: Template) => {
    if (!window.confirm(`Delete template ${t.id} · ${t.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/communications/templates/${t.id}`);
      toast.success(`Deleted ${t.id}`);
      await onChanged();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed');
    }
  };

  const openTest = (t: Template) => { setTestTarget(t); setTestTo(''); };
  const sendTest = async () => {
    if (!testTarget) return;
    if (!testTo.trim()) { toast.error(channel === 'sms' ? 'Enter a mobile number' : 'Enter an email address'); return; }
    setTestSending(true);
    try {
      const body = channel === 'sms' ? { to_phone: testTo.trim() } : { to_email: testTo.trim() };
      const r = await api.post<{ ok?: boolean; outcome?: { mocked?: boolean; provider?: string; error?: string; reason?: string } }>(
        `/communications/templates/${testTarget.id}/test`, body,
      );
      const oc = r.data?.outcome || {};
      if (r.data?.ok && oc.mocked) {
        toast.warning(`Rendered & logged, but sent via the STUB — enable ${channel === 'sms' ? 'Panacea SMS' : 'Microsoft 365 Email'} (Settings → Integrations) to actually deliver.`);
      } else if (r.data?.ok) {
        toast.success(`Test ${channel === 'sms' ? 'SMS' : 'email'} sent to ${testTo.trim()} via ${oc.provider || channel}.`);
      } else {
        toast.error(`Not sent: ${oc.error || oc.reason || 'provider error'}`);
      }
      setTestTarget(null);
      await onChanged();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Test send failed');
    } finally { setTestSending(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-xs text-raptor-navy/60">
          {channel === 'email' && (<span>Default mailbox: <b>{defaultMailboxLabel}</b>. Individual templates can override the "From" identity on the Mailboxes sub-tab.</span>)}
          {channel === 'sms' && (<span>SMS sender is fixed to your Panacea sender ID (RAPTOR). Cost per message is pulled from the gateway in real time — no manual pricing needed.</span>)}
        </div>
        <Button size="sm" variant="gradient" onClick={() => { setEditing(empty()); setCreating(true); }} data-testid={`${channel}-new-template`}>
          <Plus size={14} /> New template
        </Button>
      </div>
      {(() => {
        const unbound = triggers.filter((tr) => !(tr.bindings || {})[channel]);
        if (unbound.length === 0) return null;
        return (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="trigger-gap-banner">
            ⚠ {unbound.length} {channel.toUpperCase()} event(s) have no template assigned: <b>{unbound.map((t) => t.name).join(', ')}</b>. Edit a template and add them under "Fires on".
          </div>
        );
      })()}
      <div className="border border-border/60 rounded-md overflow-hidden">
        <table className="w-full text-sm font-body">
          <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
            <tr>
              <th className="py-2 px-3 text-left">ID</th>
              <th className="py-2 px-3 text-left">Name</th>
              <th className="py-2 px-3 text-left">Fires on</th>
              {channel === 'email' && <th className="py-2 px-3 text-left">Subject</th>}
              {channel === 'email' && <th className="py-2 px-3 text-left">Mailbox</th>}
              <th className="py-2 px-3 text-left">Status</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 && <tr><td colSpan={channel === 'email' ? 7 : 5} className="py-4 text-center text-raptor-navy/50">No templates yet.</td></tr>}
            {templates.map(t => {
              const mailbox = mailboxes.find((m) => m.id === t.mailbox_key);
              const firesOn = triggers.filter((tr) => (tr.bindings || {})[channel] === t.id);
              return (
                <tr key={t.id} className="border-t border-border/60" data-testid={`template-row-${t.id}`}>
                  <td className="py-2 px-3 font-tabular text-raptor-navy/70">{t.id}</td>
                  <td className="py-2 px-3 font-semibold text-raptor-navy">
                    {t.name}
                    {t.is_system && <Badge variant="muted" className="ml-2 text-[10px]">system</Badge>}
                  </td>
                  <td className="py-2 px-3">
                    {firesOn.length === 0
                      ? <span className="text-[11px] text-amber-600" data-testid={`template-nofire-${t.id}`}>— not linked</span>
                      : <div className="flex flex-wrap gap-1">{firesOn.map((tr) => <Badge key={tr.key} variant="outline" className="text-[10px]">{tr.name}</Badge>)}</div>}
                  </td>
                  {channel === 'email' && <td className="py-2 px-3">{t.subject}</td>}
                  {channel === 'email' && (
                    <td className="py-2 px-3">
                      {mailbox
                        ? <Badge variant="outline">{mailbox.label}</Badge>
                        : <span className="text-raptor-navy/50 text-xs">Use default</span>}
                    </td>
                  )}
                  <td className="py-2 px-3">{badge(!!t.enabled)}</td>
                  <td className="py-2 px-3">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => openTest(t)} data-testid={`template-test-${t.id}`} title={`Send a live test ${channel === 'sms' ? 'SMS' : 'email'} of this template`}>
                        <Radio size={12} /> Test
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`}>
                        <Pencil size={12} /> Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => void del(t)} data-testid={`template-delete-${t.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Send-test modal — live-fire this template to any email/phone */}
      <Dialog open={!!testTarget} onOpenChange={(o) => { if (!o) setTestTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send test {channel === 'sms' ? 'SMS' : 'email'}</DialogTitle>
            <DialogDescription>
              Delivers <b>{testTarget?.name}</b> ({testTarget?.id}) with sample values filled in, so you can preview it live on {channel === 'sms' ? 'a phone' : 'an inbox'}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{channel === 'sms' ? 'Send test SMS to (mobile number)' : 'Send test email to'}</Label>
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder={channel === 'sms' ? '+27821234567' : 'you@example.co.za'}
              type={channel === 'sms' ? 'tel' : 'email'}
              onKeyDown={(e) => { if (e.key === 'Enter') void sendTest(); }}
              data-testid="template-test-to"
            />
            <p className="text-[11px] text-raptor-navy/50">All {'{{variables}}'} render with realistic sample data.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestTarget(null)} disabled={testSending} data-testid="template-test-cancel">Cancel</Button>
            <Button variant="gradient" onClick={() => void sendTest()} disabled={testSending} data-testid="template-test-send">
              <Radio size={14} /> {testSending ? 'Sending…' : 'Send test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit / create modal */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setCreating(false); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border/60 flex-shrink-0">
            <DialogTitle>{creating ? `New ${channel.toUpperCase()} template` : `Edit ${editing?.id}`}</DialogTitle>
            <DialogDescription>
              {channel === 'email'
                ? 'Use {{variable}} placeholders anywhere. Bind to a mailbox to override the tenant default From identity.'
                : 'Keep SMS bodies concise — {{variables}} are replaced at send time.'}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-0">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="template-edit-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Input value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="template-edit-desc" />
                </div>
                {channel === 'email' && (
                  <>
                    <div className="space-y-1.5 col-span-2">
                      <Label>Subject</Label>
                      <Input
                        ref={subjectRef}
                        value={editing.subject || ''}
                        onFocus={() => { lastFocused.current = 'subject'; }}
                        onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                        data-testid="template-edit-subject"
                      />
                    </div>
                    <div className="space-y-1.5 col-span-2">
                      <Label>Mailbox (From identity)</Label>
                      <Select value={editing.mailbox_key || ''} onChange={(e) => setEditing({ ...editing, mailbox_key: e.target.value || null })} data-testid="template-edit-mailbox">
                        <option value="">— Use tenant default —</option>
                        {mailboxes.filter((m) => m.enabled).map((m) => (
                          <option key={m.id} value={m.id}>{m.label} · {m.address}</option>
                        ))}
                      </Select>
                    </div>
                  </>
                )}
              </div>

              {/* Fires on — searchable multi-select of system triggers.
                  Selecting binds THIS template to the trigger's channel slot. */}
              <div className="space-y-1.5 rounded-lg border border-raptor-blue/20 bg-raptor-blue/[0.03] p-3">
                <div className="flex items-center gap-2">
                  <Zap size={14} className="text-raptor-blue" />
                  <Label className="mb-0">Fires on {channel === 'email' ? 'email' : 'SMS'} event(s)</Label>
                </div>
                <p className="text-[11px] text-raptor-navy/50">Pick the system event(s) that send this template. Click to see all events, or type to search (e.g. "otp", "survey", "reset").</p>
                {editTriggerKeys.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {editTriggerKeys.map((k) => {
                      const tr = triggers.find((x) => x.key === k);
                      return (
                        <span key={k} className="inline-flex items-center gap-1 rounded-full bg-raptor-blue/10 text-raptor-blue text-[11px] font-medium pl-2.5 pr-1 py-0.5" data-testid={`trigger-chip-${k}`}>
                          {tr?.name || k}
                          <button type="button" onClick={() => setEditTriggerKeys((prev) => prev.filter((x) => x !== k))} className="hover:bg-raptor-blue/20 rounded-full p-0.5" data-testid={`trigger-chip-remove-${k}`}>
                            <X size={11} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="relative" ref={triggerBoxRef}>
                  <div className="relative">
                    <Input
                      value={triggerSearch}
                      onChange={(e) => { setTriggerSearch(e.target.value); setTriggerOpen(true); }}
                      onFocus={() => setTriggerOpen(true)}
                      placeholder="Search or select an event…"
                      className="pr-9"
                      data-testid="trigger-search"
                    />
                    <button
                      type="button"
                      onClick={() => setTriggerOpen((o) => !o)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-raptor-navy/50 hover:text-raptor-navy"
                      data-testid="trigger-dropdown-toggle"
                      aria-label="Toggle event list"
                    >
                      <ChevronDown size={16} className={triggerOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
                    </button>
                  </div>
                  {triggerOpen && (() => {
                    const q = triggerSearch.trim().toLowerCase();
                    const available = triggers
                      .filter((tr) => !editTriggerKeys.includes(tr.key))
                      .filter((tr) => !q
                        || (tr.name || '').toLowerCase().includes(q)
                        || (tr.key || '').toLowerCase().includes(q)
                        || (tr.description || '').toLowerCase().includes(q));
                    return (
                      <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-border/60 bg-white shadow-lg divide-y divide-border/40" data-testid="trigger-dropdown-list">
                        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-raptor-navy/40 bg-raptor-neutral/30 sticky top-0">
                          {available.length} {channel.toUpperCase()} event{available.length === 1 ? '' : 's'} available
                        </div>
                        {available.map((tr) => {
                          const taken = (tr.bindings || {})[channel];
                          return (
                            <button
                              key={tr.key} type="button"
                              onClick={() => {
                                setEditTriggerKeys((prev) => [...prev, tr.key]);
                                setTriggerSearch('');
                                // Surface the trigger's variables so the author can insert them.
                                setEditing((prev) => prev ? { ...prev, variables: Array.from(new Set([...(prev.variables || []), ...(tr.variables || [])])) } : prev);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-raptor-neutral/40 transition-colors"
                              data-testid={`trigger-option-${tr.key}`}
                            >
                              <div className="text-xs font-medium text-raptor-navy flex items-center justify-between gap-2">
                                {tr.name || tr.key}
                                {taken && taken !== editing.id && <span className="text-[10px] text-amber-600 whitespace-nowrap">reassigns from {taken}</span>}
                              </div>
                              {tr.description && <div className="text-[10px] text-raptor-navy/50 truncate">{tr.description}</div>}
                            </button>
                          );
                        })}
                        {available.length === 0 && (
                          <div className="px-3 py-2 text-[11px] text-raptor-navy/40">{q ? 'No matching events.' : 'All events are already assigned to this template.'}</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Variable picker — click a chip to insert {{var}} at cursor */}
              <VariablePicker
                template={editing}
                triggers={triggers}
                channel={channel}
                onInsert={(varName) => {
                  const token = `{{${varName}}}`;
                  if (channel === 'email' && lastFocused.current === 'subject' && subjectRef.current) {
                    const el = subjectRef.current;
                    const start = el.selectionStart ?? (editing.subject || '').length;
                    const end = el.selectionEnd ?? start;
                    const next = (editing.subject || '').slice(0, start) + token + (editing.subject || '').slice(end);
                    setEditing({ ...editing, subject: next });
                    setTimeout(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); }, 0);
                  } else if (channel === 'email' && rteRef.current) {
                    // TipTap manages its own cursor — imperative insertText
                    // drops the token at the current caret position.
                    rteRef.current.insertText(token);
                  } else if (bodyRef.current) {
                    const el = bodyRef.current;
                    const start = el.selectionStart ?? (editing.body_text || '').length;
                    const end = el.selectionEnd ?? start;
                    const next = (editing.body_text || '').slice(0, start) + token + (editing.body_text || '').slice(end);
                    setEditing({ ...editing, body_text: next });
                    setTimeout(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); }, 0);
                  }
                  const current = new Set(editing.variables || []);
                  if (!current.has(varName)) {
                    setEditing((prev) => prev ? { ...prev, variables: [...(prev.variables || []), varName] } : prev);
                  }
                }}
              />

              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <Label>Body</Label>
                  {channel === 'email' && (
                    <button
                      type="button"
                      onClick={() => setShowPreview((v) => !v)}
                      className="inline-flex items-center gap-1 text-xs text-raptor-blue hover:underline"
                      data-testid="template-preview-toggle"
                    ><Eye size={12} /> {showPreview ? 'Hide preview' : 'Preview with sample data'}</button>
                  )}
                </div>
                {channel === 'email' ? (
                  <div className={showPreview ? 'grid md:grid-cols-2 gap-3' : ''}>
                    <RichTextEditor
                      ref={rteRef}
                      value={editing.body_html || editing.body_text || ''}
                      onFocus={() => { lastFocused.current = 'body'; }}
                      onChange={(html) => {
                        // Keep both HTML and a plain-text fallback in sync
                        // so SMS-style fallback and search still work.
                        const tmp = document.createElement('div');
                        tmp.innerHTML = DOMPurify.sanitize(html);
                        setEditing((prev) => prev ? { ...prev, body_html: html, body_text: tmp.textContent || '' } : prev);
                      }}
                      minHeight={280}
                      placeholder="Compose the email body — use the toolbar for formatting and click a variable chip above to insert a token."
                      testId="template-edit-body-rte"
                    />
                    {showPreview && (
                      <TemplatePreview
                        template={editing}
                        triggers={triggers}
                        channel={channel}
                        sample={sampleVars}
                        onSampleChange={setSampleVars}
                      />
                    )}
                  </div>
                ) : (
                  <Textarea
                    ref={bodyRef}
                    rows={4}
                    value={editing.body_text || ''}
                    onFocus={() => { lastFocused.current = 'body'; }}
                    onChange={(e) => setEditing({ ...editing, body_text: e.target.value })}
                    data-testid="template-edit-body"
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Variables (comma-separated)</Label>
                <Input value={(editing.variables || []).join(', ')} onChange={(e) => setEditing({ ...editing, variables: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })} data-testid="template-edit-vars" />
              </div>
              <label className="inline-flex items-center gap-2 text-sm font-body">
                <input type="checkbox" checked={!!editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} data-testid="template-edit-enabled" />
                Enabled
              </label>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-border/60 bg-white/95 flex-shrink-0">
            <Button variant="outline" onClick={() => { setEditing(null); setCreating(false); }} disabled={saving} data-testid="template-edit-cancel">Cancel</Button>
            <Button variant="gradient" onClick={() => editing && void save(editing)} disabled={saving} data-testid="template-edit-save">
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Triggers table (channel-scoped)
// ═══════════════════════════════════════════════════════════════════════
interface TriggerTableProps { channel: 'email' | 'sms'; triggers: Trigger[]; templates: Template[]; onChanged: () => Promise<void> }
const TriggerTable: React.FC<TriggerTableProps> = ({ channel, triggers, templates, onChanged }) => {
  const [testing, setTesting] = useState<string | null>(null);
  // Show every trigger so an operator can bind a template to ANY event on
  // this channel — including system triggers that have no binding yet.
  const filtered = triggers;
  const bindTemplate = async (tr: Trigger, tid: string) => {
    try {
      await api.patch(`/communications/triggers/${tr.id}`, { bindings: { ...(tr.bindings || {}), [channel]: tid || null } });
      toast.success(`Binding updated for ${tr.name}`);
      await onChanged();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to bind');
    }
  };
  const sendTest = async (tr: Trigger) => {
    const isSms = channel === 'sms';
    const to = window.prompt(isSms ? 'Send a test SMS to (e.g. +27821234567):' : 'Send a test email to:');
    if (!to) return;
    setTesting(tr.id);
    try {
      const r = await api.post<{ outcomes?: Record<string, { ok?: boolean; mocked?: boolean; provider?: string; reason?: string; error?: string }> }>(
        '/communications/dispatch',
        { trigger_key: tr.key, channels: [channel], variables: {}, ...(isSms ? { to_phone: to } : { to_email: to }) },
      );
      const oc = (r.data?.outcomes || {})[channel] || {};
      if (oc.ok && oc.mocked) {
        toast.warning('Rendered & logged, but sent via the STUB — enable Microsoft 365 Email (Settings → Integrations) to actually deliver.');
      } else if (oc.ok) {
        toast.success(`Test sent via ${oc.provider || channel}.`);
      } else {
        toast.error(`Not sent: ${oc.reason || oc.error || 'no template bound / provider error'}`);
      }
      await onChanged();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Test dispatch failed');
    } finally {
      setTesting(null);
    }
  };
  return (
    <div className="space-y-3">
      <div className="text-xs text-raptor-navy/60">
        Each trigger fires on a real system event and dispatches its bound template on this channel. Pick a template to bind it — leave it blank to skip this channel. Use <b>Send test</b> to fire it now to a recipient of your choice.
      </div>
      <div className="border border-border/60 rounded-md overflow-hidden">
        <table className="w-full text-sm font-body">
          <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
            <tr>
              <th className="py-2 px-3 text-left">Trigger</th>
              <th className="py-2 px-3 text-left">Description</th>
              <th className="py-2 px-3 text-left">Bound {channel} template</th>
              <th className="py-2 px-3 text-left">Status</th>
              <th className="py-2 px-3 text-right w-24">Test</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-raptor-navy/50">No {channel} triggers.</td></tr>}
            {filtered.map((tr) => {
              const bound = (tr.bindings || {})[channel] || '';
              return (
              <tr key={tr.id} className="border-t border-border/60" data-testid={`trigger-row-${tr.key}`}>
                <td className="py-2 px-3 font-semibold text-raptor-navy">
                  {tr.name}
                  <div className="text-[11px] text-raptor-navy/50 font-tabular">{tr.key}</div>
                </td>
                <td className="py-2 px-3 text-raptor-navy/70 max-w-md">{tr.description}</td>
                <td className="py-2 px-3">
                  <Select value={bound} onChange={(e) => void bindTemplate(tr, e.target.value)} data-testid={`trigger-bind-${tr.key}`}>
                    <option value="">— None —</option>
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.id} · {t.name}</option>)}
                  </Select>
                </td>
                <td className="py-2 px-3">{badge(!!tr.enabled)}</td>
                <td className="py-2 px-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="whitespace-nowrap"
                    disabled={!bound || testing === tr.id}
                    onClick={() => void sendTest(tr)}
                    data-testid={`trigger-test-${tr.key}`}
                  >
                    {testing === tr.id ? 'Sending…' : 'Send test'}
                  </Button>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// Mailboxes panel
// ═══════════════════════════════════════════════════════════════════════
interface MailboxesPanelProps { mailboxes: Mailbox[]; onChanged: () => Promise<void> }
const MailboxesPanel: React.FC<MailboxesPanelProps> = ({ mailboxes, onChanged }) => {
  const [editing, setEditing] = useState<Partial<Mailbox> | null>(null);
  const save = async () => {
    if (!editing) return;
    if (!editing.label || !editing.address) {
      toast.error('Label and address are required');
      return;
    }
    try {
      const payload = { label: editing.label, address: editing.address, display_name: editing.display_name, reply_to: editing.reply_to, is_default: !!editing.is_default, enabled: editing.enabled !== false };
      if (editing.id) {
        await api.patch(`/communications/mailboxes/${editing.id}`, payload);
      } else {
        await api.post('/communications/mailboxes', payload);
      }
      toast.success('Mailbox saved');
      setEditing(null);
      await onChanged();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed');
    }
  };
  const del = async (m: Mailbox) => {
    if (!window.confirm(`Delete mailbox "${m.label}" (${m.address})?\nTemplates using it will fall back to the default.`)) return;
    try {
      await api.delete(`/communications/mailboxes/${m.id}`);
      toast.success('Mailbox deleted');
      await onChanged();
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed');
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center gap-3">
        <div className="text-xs text-raptor-navy/60">
          Register the sending mailboxes RAPTOR is allowed to use. The <b>default</b> identity is used whenever a template has no explicit binding. Each mailbox must exist in your Microsoft 365 tenant — set them up via Settings → Integrations → Microsoft 365 Email.
        </div>
        <Button size="sm" variant="gradient" className="whitespace-nowrap shrink-0" onClick={() => setEditing({ label: '', address: '', is_default: mailboxes.length === 0, enabled: true })} data-testid="mailbox-new">
          <Plus size={14} /> Add mailbox
        </Button>
      </div>
      <div className="border border-border/60 rounded-md overflow-hidden">
        <table className="w-full text-sm font-body">
          <thead className="bg-raptor-neutral/40 text-xs uppercase text-raptor-navy/70">
            <tr>
              <th className="py-2 px-3 text-left">Label</th>
              <th className="py-2 px-3 text-left">Address</th>
              <th className="py-2 px-3 text-left">Display name</th>
              <th className="py-2 px-3 text-left">Reply-to</th>
              <th className="py-2 px-3 text-left">Default</th>
              <th className="py-2 px-3 text-left">Status</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mailboxes.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-raptor-navy/50">No mailboxes yet — add your first "From" identity.</td></tr>}
            {mailboxes.map((m) => (
              <tr key={m.id} className="border-t border-border/60" data-testid={`mailbox-row-${m.id}`}>
                <td className="py-2 px-3 font-semibold text-raptor-navy">{m.label}</td>
                <td className="py-2 px-3 font-tabular">{m.address}</td>
                <td className="py-2 px-3">{m.display_name || <span className="text-raptor-navy/40">—</span>}</td>
                <td className="py-2 px-3 font-tabular text-raptor-navy/70">{m.reply_to || <span className="text-raptor-navy/40">—</span>}</td>
                <td className="py-2 px-3">{m.is_default ? <Badge variant="success"><Star size={10} className="mr-1" /> Default</Badge> : <span className="text-raptor-navy/40">—</span>}</td>
                <td className="py-2 px-3">{badge(m.enabled)}</td>
                <td className="py-2 px-3">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setEditing(m)} data-testid={`mailbox-edit-${m.id}`}>
                      <Pencil size={12} /> Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => void del(m)} data-testid={`mailbox-delete-${m.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit mailbox' : 'Add mailbox'}</DialogTitle>
            <DialogDescription>Must resolve to a real licensed Microsoft 365 mailbox in your tenant.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Label</Label>
                <Input value={editing.label || ''} onChange={(e) => setEditing({ ...editing, label: e.target.value })} data-testid="mailbox-form-label" />
              </div>
              <div className="space-y-1.5"><Label>Email address</Label>
                <Input type="email" value={editing.address || ''} onChange={(e) => setEditing({ ...editing, address: e.target.value })} data-testid="mailbox-form-address" />
              </div>
              <div className="space-y-1.5"><Label>Display name (optional)</Label>
                <Input value={editing.display_name || ''} onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} data-testid="mailbox-form-display" />
              </div>
              <div className="space-y-1.5"><Label>Reply-to (optional)</Label>
                <Input type="email" value={editing.reply_to || ''} onChange={(e) => setEditing({ ...editing, reply_to: e.target.value })} data-testid="mailbox-form-replyto" />
              </div>
              <div className="flex gap-4">
                <label className="inline-flex items-center gap-2 text-sm font-body">
                  <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} data-testid="mailbox-form-default" />
                  Set as tenant default
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-body">
                  <input type="checkbox" checked={editing.enabled !== false} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} data-testid="mailbox-form-enabled" />
                  Enabled
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="gradient" onClick={() => void save()} data-testid="mailbox-form-save"><Save size={14} /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// SMS Pricing panel (backs the Reports > SMS spend widget)
// ═══════════════════════════════════════════════════════════════════════
const SmsPricingPanel: React.FC = () => {
  const [cost, setCost] = useState<number>(0.30);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try {
      const r = await api.get<{ unit_cost_zar: number }>('/communications/reports/sms-pricing');
      setCost(r.data.unit_cost_zar);
    } catch (err) { console.warn('sms pricing load failed', err); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put('/communications/reports/sms-pricing', { unit_cost_zar: cost });
      toast.success('SMS pricing updated');
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed');
    } finally { setSaving(false); }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>SMS unit cost</CardTitle>
        <CardDescription>Per-segment cost used to compute Reports → SMS spend. Update whenever your carrier renegotiates rates.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-3">
          <div className="space-y-1.5">
            <Label>Cost per SMS segment (ZAR)</Label>
            <Input type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(Number(e.target.value))} data-testid="sms-pricing-input" className="w-48" />
          </div>
          <Button variant="gradient" onClick={() => void save()} disabled={saving} data-testid="sms-pricing-save">
            <Save size={14} /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// REPORTS TAB
// ═══════════════════════════════════════════════════════════════════════
const ReportsTab: React.FC = () => {
  const [email, setEmail] = useState<EmailReport | null>(null);
  const [sms, setSms] = useState<SmsSpend | null>(null);
  const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (range.from) p.set('from_date', range.from);
      if (range.to) p.set('to_date', range.to);
      const qs = p.toString() ? `?${p.toString()}` : '';
      const [e, s] = await Promise.all([
        api.get<EmailReport>(`/communications/reports/email${qs}`),
        api.get<SmsSpend>(`/communications/reports/sms-spend${qs}`),
      ]);
      setEmail(e.data);
      setSms(s.data);
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to load reports');
    } finally { setLoading(false); }
  }, [range.from, range.to]);
  useEffect(() => { void load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end p-3 bg-raptor-neutral/40 rounded-md">
        <div className="space-y-1.5"><Label>From</Label>
          <Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="reports-from" />
        </div>
        <div className="space-y-1.5"><Label>To</Label>
          <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="reports-to" />
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} data-testid="reports-refresh">
          <RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card data-testid="reports-email-card">
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2"><Mail size={16} /> Email deliverability</CardTitle>
            <CardDescription>Success and failure counts from the communication log.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              <StatTile label="Delivered" value={email?.delivered ?? 0} icon={<CheckCircle2 size={14} className="text-emerald-600" />} />
              <StatTile label="Failed" value={email?.failed ?? 0} icon={<XCircle size={14} className="text-red-600" />} />
              <StatTile label="Queued" value={email?.queued ?? 0} icon={<RefreshCw size={14} className="text-raptor-blue" />} />
            </div>
            <RecentTable rows={email?.recent ?? []} channel="email" />
          </CardContent>
        </Card>

        <Card data-testid="reports-sms-card">
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2"><MessageSquare size={16} /> SMS spend</CardTitle>
            <CardDescription>Real-time cost against the configured per-segment rate.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              <StatTile label="Segments sent" value={sms?.total_segments ?? 0} />
              <StatTile label="Failed" value={sms?.total_failed ?? 0} icon={<XCircle size={14} className="text-red-600" />} />
              <StatTile label="Total spend" value={`R ${(sms?.total_spend_zar ?? 0).toFixed(2)}`} />
            </div>
            <div className="text-[11px] text-raptor-navy/50 mt-2 font-body">
              {sms?.unit_cost_source === 'panacea_real_cost'
                ? <span>Live cost from Panacea gateway · <b>~R{(sms?.unit_cost_zar ?? 0).toFixed(4)}</b> per segment average</span>
                : <span className="italic">Estimated @ R{(sms?.unit_cost_zar ?? 0.30).toFixed(2)}/seg — will switch to live gateway cost as soon as real sends flow through</span>}
            </div>
            <RecentTable rows={sms?.recent ?? []} channel="sms" />
          </CardContent>
        </Card>
      </div>

      <GraphSubscriptionsCard />
    </div>
  );
};

const StatTile: React.FC<{ label: string; value: number | string; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <div className="p-3 bg-raptor-neutral/30 rounded-md">
    <div className="text-[10px] uppercase text-raptor-navy/60 flex items-center gap-1">{icon}{label}</div>
    <div className="text-2xl font-bold font-tabular text-raptor-navy">{value}</div>
  </div>
);

const RecentTable: React.FC<{ rows: Array<Record<string, unknown>>; channel: string }> = ({ rows, channel }) => {
  if (rows.length === 0) {
    return <div className="text-center py-4 text-raptor-navy/50 text-xs mt-3">No {channel} events yet in this window.</div>;
  }
  return (
    <div className="mt-3 border border-border/60 rounded-md overflow-hidden max-h-72 overflow-y-auto">
      <table className="w-full text-xs font-body">
        <thead className="bg-raptor-neutral/40 sticky top-0">
          <tr>
            <th className="py-1.5 px-2 text-left">When</th>
            <th className="py-1.5 px-2 text-left">To</th>
            <th className="py-1.5 px-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.id || i}`} className="border-t border-border/40">
              <td className="py-1 px-2 font-tabular text-raptor-navy/60">{String(r.created_at || '').slice(0, 19).replace('T', ' ')}</td>
              <td className="py-1 px-2 font-tabular">{String(r.recipient || r.to || '—')}</td>
              <td className="py-1 px-2">
                <Badge variant={String(r.status) === 'delivered' || String(r.status) === 'ok' ? 'success' : String(r.status) === 'failed' ? 'destructive' : 'muted'}>
                  {String(r.status || 'unknown')}
                </Badge>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CommunicationsSettings;

/* ═══════════════════════════════════════════════════════════════════════
 * Microsoft Graph subscription status card (Reports tab footer)
 * ═══════════════════════════════════════════════════════════════════════
 * When MS Graph mail is enabled, we can register change-notification
 * subscriptions so Graph POSTs delivery events into
 * `/api/webhooks/graph/notifications`. The Reports tab then flips
 * rows from `queued` to `delivered` in near-real time — no refresh.
 */
interface GraphSubscription {
  id: string;
  mailbox: string;
  resource: string;
  change_type: string;
  expires_at: string;
  last_renewed_at?: string | null;
  created_at?: string;
}
interface GraphSubsPayload {
  enabled: boolean;
  webhook_url: string | null;
  default_sender: string | null;
  subscriptions: GraphSubscription[];
}

const GraphSubscriptionsCard: React.FC = () => {
  const [data, setData] = useState<GraphSubsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [mailbox, setMailbox] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get<GraphSubsPayload>('/communications/graph/subscriptions');
      setData(r.data);
      if (!mailbox && r.data.default_sender) setMailbox(r.data.default_sender);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Failed to load Graph subscriptions');
    } finally { setLoading(false); }
  }, [mailbox]);
  useEffect(() => { void load(); }, [load]);

  const subscribe = useCallback(async () => {
    if (!mailbox.trim()) { toast.error('Enter a mailbox address to subscribe.'); return; }
    setBusy('create');
    try {
      const r = await api.post<{ subscription_id: string; expires_at: string }>('/communications/graph/subscriptions', { mailbox: mailbox.trim() });
      toast.success(`Subscription ${r.data.subscription_id.slice(0, 8)}… created`);
      await load();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Subscribe failed');
    } finally { setBusy(null); }
  }, [mailbox, load]);

  const renew = useCallback(async (id: string) => {
    setBusy(id);
    try {
      await api.post(`/communications/graph/subscriptions/${id}/renew`);
      toast.success('Renewal triggered');
      await load();
    } catch { toast.error('Renew failed'); }
    finally { setBusy(null); }
  }, [load]);

  const remove = useCallback(async (id: string) => {
    if (!window.confirm('Remove this subscription? Graph will stop sending delivery events for this mailbox.')) return;
    setBusy(id);
    try {
      await api.delete(`/communications/graph/subscriptions/${id}`);
      toast.success('Subscription removed');
      await load();
    } catch { toast.error('Remove failed'); }
    finally { setBusy(null); }
  }, [load]);

  const enabled = !!data?.enabled;

  return (
    <Card data-testid="graph-subs-card">
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2"><Radio size={16} /> Real-time delivery status (Microsoft Graph)</CardTitle>
        <CardDescription>
          Register a webhook subscription so Graph pushes delivery + bounce events into
          the Reports view without a manual refresh.
          {enabled
            ? <span className="text-emerald-700"> Integration is enabled — you are ready to subscribe.</span>
            : <span className="text-amber-700"> Enable the Microsoft Graph mail integration under Settings › Integrations first.</span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 items-end p-3 bg-raptor-neutral/30 rounded-md">
          <div className="space-y-1.5 flex-1 min-w-[240px]">
            <Label>Mailbox</Label>
            <Input
              value={mailbox}
              onChange={(e) => setMailbox(e.target.value)}
              placeholder={data?.default_sender || 'no-reply@ospreyunderwriting.com'}
              disabled={!enabled}
              data-testid="graph-sub-mailbox"
            />
          </div>
          <Button
            size="sm" variant="default"
            onClick={() => void subscribe()}
            disabled={!enabled || busy === 'create'}
            data-testid="graph-sub-subscribe"
          >
            <Zap size={14} /> {busy === 'create' ? 'Subscribing…' : 'Subscribe'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading} data-testid="graph-sub-refresh">
            <RefreshCw size={14} /> Refresh
          </Button>
        </div>

        {data?.webhook_url && (
          <div className="mt-2 text-[11px] text-raptor-navy/50 font-tabular">
            Notifications URL: <span className="font-semibold text-raptor-navy/70">{data.webhook_url}</span>
          </div>
        )}

        <div className="mt-3 border border-border/60 rounded-md overflow-hidden">
          <table className="w-full text-xs font-body">
            <thead className="bg-raptor-neutral/40 text-[10px] uppercase text-raptor-navy/70">
              <tr>
                <th className="py-1.5 px-2 text-left">Subscription</th>
                <th className="py-1.5 px-2 text-left">Mailbox</th>
                <th className="py-1.5 px-2 text-left">Resource</th>
                <th className="py-1.5 px-2 text-left">Expires</th>
                <th className="py-1.5 px-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(!data?.subscriptions || data.subscriptions.length === 0) && (
                <tr><td colSpan={5} className="py-4 text-center text-raptor-navy/50">
                  {enabled
                    ? 'No active subscriptions. Click Subscribe above to start receiving real-time events.'
                    : 'Enable Microsoft Graph mail to see and manage subscriptions here.'}
                </td></tr>
              )}
              {(data?.subscriptions || []).map((s) => (
                <tr key={s.id} className="border-t border-border/40" data-testid={`graph-sub-row-${s.id}`}>
                  <td className="py-1.5 px-2 font-tabular text-raptor-navy/70">{s.id.slice(0, 12)}…</td>
                  <td className="py-1.5 px-2">{s.mailbox}</td>
                  <td className="py-1.5 px-2 text-[10px] text-raptor-navy/60 truncate max-w-[240px]">{s.resource}</td>
                  <td className="py-1.5 px-2 font-tabular">
                    {String(s.expires_at || '').slice(0, 19).replace('T', ' ')}
                  </td>
                  <td className="py-1.5 px-2 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => void renew(s.id)} disabled={busy === s.id}
                            data-testid={`graph-sub-renew-${s.id}`}>
                      <RefreshCw size={12} /> Renew
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => void remove(s.id)} disabled={busy === s.id}
                            data-testid={`graph-sub-remove-${s.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

